import type { InferRequestType } from 'hono/client'
import { api, json } from '../client.ts'
import type { Exercise } from '../types.ts'

export type ExercisePatch = InferRequestType<(typeof api.exercises)[':id']['$patch']>['json']

/** Any text the server requires that is empty or spaces: what it refuses with "A required field is empty." */
const hasBlank = (v: unknown): boolean =>
	typeof v === 'string'
		? v.trim() === ''
		: Array.isArray(v)
			? v.some(hasBlank)
			: v !== null && typeof v === 'object' && Object.values(v).some(hasBlank)

/** Whether an edit (a patch or a draft) holds a box she has emptied. */
export const patchHasBlank = (p: { prompt?: unknown; data?: unknown; answer?: unknown }): boolean =>
	hasBlank([p.prompt, p.data, p.answer])

/**
 * One autosave request. A 400 for an edit that holds a blank box is her retyping: the editor says so
 * beside the box and saves once it is filled, so it is not a problem report. Any other 400 (the web
 * and the api disagreeing on a field, say), and every 5xx, is reported with the api's detail.
 * `remarked`: answers already handed in that a fixed key moved. `remarkLater`: a written key was
 * saved and its re-mark waits until she stops editing it. Both absent from an older api.
 */
export const patchExercise = async (id: string, patch: ExercisePatch) => {
	const res = await api.exercises[':id'].$patch({ param: { id }, json: patch })
	const out = await json<{ exercise: Exercise; remarked?: number; remarkLater?: boolean }>(
		res,
		patchHasBlank(patch) ? [400] : [],
	)
	return {
		exercise: out.exercise,
		remarked: out.remarked ?? 0,
		remarkLater: out.remarkLater ?? false,
	}
}
